/**
 * Numeric form-field parsing that keeps "the operator typed nothing" and "the
 * operator typed 0" as different facts.
 *
 * WHY THIS EXISTS
 * ───────────────
 * `Number('')` is `0`. So does `Number('  ')`, and `+''`. A blank field coerced
 * straight into a request body therefore becomes a value NOBODY TYPED — and
 * because `0` is finite, integral, non-negative and `<= 1`, every shape of the
 * usual guard waves it through:
 *
 *     Number.isFinite(Number(raw))             // true for ''
 *     Number.isFinite(Number(raw)) && n >= 0   // true for ''
 *     Number.isFinite(Number(raw)) && n <= 1   // true for ''
 *
 * This is not hypothetical in this repo. A supervisory motion dispatch turned a
 * cleared "Target position" into an absolute move to position 0 on real drive
 * hardware; a champion→challenger console recorded a fabricated 0-vs-0 A/B
 * result that a model promotion was then judged on. Two packages that share no
 * code reached the same defect independently, which is what makes it a class
 * rather than two bugs.
 *
 * The two "fixes" that look like fixes and are not:
 *
 *   • `Number(raw) || 0` and `Number(raw) ?? 0` — these SUBSTITUTE the
 *     fabricated value instead of refusing it. `?? 0` never even fires, because
 *     `0` is not nullish. And `||` has a second, opposite failure: it also
 *     discards a deliberately typed `0`, so `deadband: Number(raw) || 0` makes
 *     "cleared" and "deliberately zero" indistinguishable in BOTH directions.
 *   • `parseInt` / `parseFloat` — these yield `NaN` for a blank field, which a
 *     JSON body carries as `null` (a governed write recording "no value" where
 *     the operator recorded nothing) or which the `|| 0` that usually follows
 *     launders straight back into `0`.
 *
 * WHAT THESE HELPERS DO INSTEAD
 * ─────────────────────────────
 * They decide emptiness BEFORE coercing, and return `number | null` — a stated
 * absence rather than a substituted value. `'0'` parses to `0`; `''` parses to
 * `null`. What a `null` MEANS is then the caller's decision to make out loud,
 * and there are only three honest answers:
 *
 *   1. **The field is required.** Block the submit and say why
 *      (`missingNumericFieldsReason`). Never put a number on the wire.
 *   2. **Blank has a documented default.** Write it explicitly and name it:
 *      `parseNumericInput(raw) ?? DEFAULT_WINDOW_HOURS`. This is safe precisely
 *      because the left side is `number | null`, so a typed `0` still wins the
 *      `??` — which is the whole reason `|| DEFAULT` was wrong.
 *   3. **Blank means "no edit".** For a live editor bound to a numeric model,
 *      return early and leave the model holding its last real value rather than
 *      patching it to 0.
 *
 * These take the raw text as a PARAMETER rather than reading a state binding,
 * which is also why `tyndo/no-unguarded-numeric-coercion` is silent on them by
 * construction: extracting the decision into a validated-value helper is the
 * shape that rule pushes towards.
 */

/** The raw contents of a numeric control, as the DOM and React state hold it. */
export type RawNumericInput = string | number | null | undefined;

/**
 * Parse one numeric field, or report that it carries no number at all.
 *
 * Returns `null` — never a substituted default — when the field is `null`,
 * `undefined`, empty, whitespace-only, non-numeric, or names a non-finite value
 * (`Infinity`, `1e999`, `NaN`), which JSON cannot carry and no setpoint can
 * hold. An explicitly typed `0` returns `0`.
 *
 * A `number` argument is passed through when finite, so a field whose state has
 * already been narrowed to a number does not need a different call.
 */
export function parseNumericInput(raw: RawNumericInput): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Parse one whole-number field (a count, a quantity, a sequence, a score on an
 * integer scale), or report that it carries no such number.
 *
 * Non-integers are REFUSED rather than truncated. This is the deliberate
 * difference from the `parseInt(raw)` these call sites used to use: `parseInt`
 * silently reads `'1.9'` as `1`, so a counted quantity of 1.9 becomes a
 * recorded count of 1 that nobody typed either — the same fabrication as the
 * blank field, one digit further along.
 */
export function parseIntegerInput(raw: RawNumericInput): number | null {
  const value = parseNumericInput(raw);
  return value !== null && Number.isInteger(value) ? value : null;
}

/**
 * True when EVERY listed field carries a number. Intended for a `disabled=`
 * expression so the button and the submit handler read one rule instead of each
 * re-deriving it. A blank field makes the whole set incomplete.
 */
export function numericInputsComplete(...raws: readonly RawNumericInput[]): boolean {
  return raws.every((raw) => parseNumericInput(raw) !== null);
}

/** One numeric field on a form, with the label the view renders for it. */
export interface NumericFieldSpec<TKey extends string = string> {
  key: TKey;
  label: string;
  /** Refuse non-integers as well as blanks (a count, a sequence, a 1–10 score). */
  integer?: boolean;
}

/** The outcome of parsing a whole form's numeric fields. */
export interface ParsedNumericFields<TKey extends string = string> {
  /**
   * The parsed values — `null` when ANY field failed, so a caller cannot
   * accidentally submit a partially-typed body.
   */
  values: Record<TKey, number> | null;
  /** The fields carrying no number, in the order they were specified. */
  missing: NumericFieldSpec<TKey>[];
}

/**
 * Parse every numeric field of a form at once. `values` is non-null only when
 * ALL of them carry an explicitly typed number, which is what makes the
 * "either the whole body is real or none of it goes" property enforceable at
 * one call site rather than field by field.
 */
export function parseNumericFields<TKey extends string>(
  specs: readonly NumericFieldSpec<TKey>[],
  raw: Readonly<Partial<Record<TKey, RawNumericInput>>>,
): ParsedNumericFields<TKey> {
  const values = {} as Record<TKey, number>;
  const missing: NumericFieldSpec<TKey>[] = [];

  for (const spec of specs) {
    const parsed = spec.integer
      ? parseIntegerInput(raw[spec.key])
      : parseNumericInput(raw[spec.key]);
    if (parsed === null) missing.push(spec);
    else values[spec.key] = parsed;
  }

  return { values: missing.length === 0 ? values : null, missing };
}

/**
 * The stated reason a submit is unavailable, or `null` when nothing is missing.
 *
 * The wording names the offending fields and says out loud that a blank field
 * is not a zero: an operator has to be able to tell why a button is inert
 * without guessing, and the sentence is also the thing that stops the next
 * reader from "fixing" the inert button with a `|| 0`.
 */
export function missingNumericFieldsReason(
  missing: readonly NumericFieldSpec[],
): string | null {
  if (missing.length === 0) return null;
  const labels = missing.map((field) => field.label).join(', ');
  return missing.length === 1
    ? `Enter a number for ${labels} — an empty field is not zero, and nothing is submitted without it.`
    : `Enter a number for these fields: ${labels} — an empty field is not zero, and nothing is submitted without them.`;
}
